import type { BigIntish } from '@morpho-org/blue-sdk'
import type { InterfaceInputToken } from '@morpho-org/dapp-core'

import { formatLongString, isDefined } from '@morpho-org/morpho-ts'
import { abbreviateAddress } from '@repo/utils'

import { formatters } from './formatters'

/**
 * Fallback used when a token carries no symbol.
 */
export const UNKNOWN_SYMBOL = 'UNKNOWN'

/**
 * Separates the loan asset from the trust assumption in text. Visual surfaces draw
 * it as a rule instead of a character (see `MarketIdentity`).
 */
const MARKET_NAME_DIVIDER = '|'

const formatSymbol = (symbol?: string, maxLength?: number) => {
  return formatLongString(symbol || UNKNOWN_SYMBOL, maxLength)
}

/**
 * Formats an 18-decimal LLTV as a percentage — `860000000000000000n` -> `"86%"`.
 *
 * Single source of truth so the market-name string and its rendered badge never
 * drift apart (`86%` vs `86.00%`).
 *
 * @param lltv - Liquidation LTV, 18 decimals.
 * @returns The formatted percentage, or `null` when the LLTV is missing.
 */
export const formatLltv = (lltv: BigIntish | null | undefined): string | null => {
  if (!isDefined(lltv)) {
    return null
  }

  return formatters.percent.digits(2).unit('%').trailingZero(false).of(BigInt(lltv), 18)
}

/**
 * Assembles a market name from its parts.
 *
 * Owns the divider, its spacing, the collateral/LLTV join and the symbol fallbacks,
 * so the surfaces that hold symbol strings rather than tokens (page metadata, sort
 * keys, mobile headers) cannot drift on any of them.
 *
 * Nullish and empty symbols mean different things, and callers used to get the
 * distinction wrong in both directions. `null` or `undefined` collateral means the
 * market has no collateral side — an idle-liquidity market, named after its loan
 * asset alone. An empty symbol means it has one whose name we don't know, so it
 * falls back to {@link UNKNOWN_SYMBOL} and keeps the divider.
 *
 * @param loanSymbol - The loan asset symbol — the market's headline identity.
 * @param collateralSymbol - The collateral symbol. Nullish for idle-liquidity markets.
 * @param formattedLltv - An already-formatted LLTV, e.g. `"86%"` (see {@link formatLltv}).
 * @returns e.g. `"USDC | cbBTC 86%"`, or the loan symbol alone when there is no collateral.
 */
export const formatMarketName = (
  loanSymbol?: string | null,
  collateralSymbol?: string | null,
  formattedLltv?: string | null
): string => {
  const loan = loanSymbol || UNKNOWN_SYMBOL

  if (collateralSymbol == null) {
    return loan
  }

  const collateral = collateralSymbol || UNKNOWN_SYMBOL
  const backing = formattedLltv ? `${collateral} ${formattedLltv}` : collateral

  return `${loan} ${MARKET_NAME_DIVIDER} ${backing}`
}

export interface GetMarketNameParams {
  loanAsset: InterfaceInputToken
  collateralAsset?: InterfaceInputToken | null
  /** Liquidation LTV (18 decimals). Appended to the backing when provided. */
  lltv?: BigIntish | null
  maxLength?: number
}

export interface MarketNameParts {
  /** Resolved loan symbol — the market's headline identity. */
  loanSymbol: string
  /** Resolved collateral symbol, or `null` for an idle-liquidity market. */
  collateralSymbol: string | null
  /** Formatted LLTV (e.g. `"86%"`), or `null` when absent or idle. */
  formattedLltv: string | null
}

/**
 * Resolve the pieces of a market's identity, applying the symbol fallbacks and
 * truncation once so the name string and its rendered counterpart never diverge.
 *
 * Prefer {@link getMarketName} for text; reach for the parts only when a surface
 * needs to lay them out itself (see `MarketIdentity`).
 */
export const getMarketNameParts = ({
  loanAsset,
  collateralAsset,
  lltv,
  maxLength
}: GetMarketNameParams): MarketNameParts => {
  if (!collateralAsset) {
    return {
      loanSymbol: loanAsset.symbol || abbreviateAddress(loanAsset.address),
      collateralSymbol: null,
      formattedLltv: null
    }
  }

  return {
    // `formatSymbol` is a no-op when `maxLength` is undefined.
    loanSymbol: formatSymbol(loanAsset.symbol, maxLength),
    collateralSymbol: formatSymbol(collateralAsset.symbol, maxLength),
    formattedLltv: formatLltv(lltv)
  }
}

/**
 * Get the canonical market name: `<loan> | <collateral> <lltv>`.
 *
 * Leads with the loan asset so a market never reads as a base/quote trading pair —
 * the collateral and its LLTV are the trust assumption, shown after the divider.
 * Idle-liquidity markets have no collateral and are named after their loan asset alone.
 *
 * @param params - The loan asset, optional collateral asset, optional LLTV and max symbol length.
 * @returns The market name.
 */
export const getMarketName = (params: GetMarketNameParams): string => {
  const { loanSymbol, collateralSymbol, formattedLltv } = getMarketNameParts(params)

  return formatMarketName(loanSymbol, collateralSymbol, formattedLltv)
}
